import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { makeVariablePattern, VARIABLE_PRESENCE_PATTERN, normalizeVariableLabel } from '../TemplateVariables.utils';
import { useTemplateVariables } from '../TemplateVariablesContext';
import styles from './VariablePreviewLine.module.scss';
export const GetVariablePreviewLineTID = (parentTestId)=>getTestIds(parentTestId);
function buildPreviewSegments(text, resolvedValues) {
    const segments = [];
    let lastIndex = 0;
    for (const match of text.matchAll(makeVariablePattern())){
        const matchStart = match.index;
        if (matchStart > lastIndex) {
            segments.push({
                type: 'text',
                value: text.slice(lastIndex, matchStart)
            });
        }
        const label = match[1].trim();
        const resolved = resolvedValues[normalizeVariableLabel(label)];
        if (resolved !== undefined) {
            segments.push({
                type: 'resolved',
                value: resolved
            });
        } else {
            segments.push({
                type: 'unresolved',
                label
            });
        }
        lastIndex = matchStart + match[0].length;
    }
    if (lastIndex < text.length) {
        segments.push({
            type: 'text',
            value: text.slice(lastIndex)
        });
    }
    return segments;
}
function hasVariables(text) {
    return VARIABLE_PRESENCE_PATTERN.test(text);
}
export function VariablePreviewLine(param) {
    let { text, resolvedValues, testId } = param;
    const { VariablePreviewLineTestIds } = useMemo(()=>GetVariablePreviewLineTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.templateVariables.preview');
    const { showVariableErrors } = useTemplateVariables();
    const containsVariables = useMemo(()=>hasVariables(text), [
        text
    ]);
    const segments = useMemo(()=>buildPreviewSegments(text, resolvedValues), [
        text,
        resolvedValues
    ]);
    if (!containsVariables) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.previewLine,
        testId: VariablePreviewLineTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: "secondary",
                weight: "bold",
                children: t.label()
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraph3",
                skin: "secondary",
                className: styles.previewContent,
                testId: VariablePreviewLineTestIds.PreviewContent(),
                children: segments.map((segment, index)=>{
                    const key = "".concat(segment.type, "-").concat(index);
                    switch(segment.type){
                        case 'text':
                            return /*#__PURE__*/ _jsx("span", {
                                children: segment.value
                            }, key);
                        case 'resolved':
                            return /*#__PURE__*/ _jsx("span", {
                                className: styles.resolvedValue,
                                children: segment.value
                            }, key);
                        case 'unresolved':
                            return /*#__PURE__*/ _jsxs("span", {
                                className: showVariableErrors ? styles.unresolvedLabelError : styles.unresolvedLabel,
                                children: [
                                    "[",
                                    segment.label,
                                    "]"
                                ]
                            }, key);
                    }
                })
            })
        ]
    });
}
